import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, TrendingUp, ShieldAlert, Send, Activity, Loader2 } from "lucide-react";
import { AppShell } from "@/components/app-shell/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel, PanelHeader } from "@/components/common/Card";
import { OutcomeBadge } from "@/components/common/OutcomeBadge";
import { StatusDot } from "@/components/common/StatusDot";
import { CodeBlock } from "@/components/common/CodeBlock";
import { api } from "@/lib/api";
import { fmtTsShort, truncateMid } from "@/lib/format";

export const Route = createFileRoute("/")({
  component: OverviewPage,
});

function Kpi({
  label,
  value,
  hint,
  delta,
  icon: Icon,
  tone = "neutral",
}: {
  label: string;
  value: string;
  hint?: string;
  delta?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: "neutral" | "ember" | "success";
}) {
  return (
    <Panel className="fade-up p-5">
      <div className="flex items-start justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
          {label}
        </span>
        <Icon
          className={
            tone === "ember"
              ? "h-4 w-4 text-ember"
              : tone === "success"
                ? "h-4 w-4 text-success"
                : "h-4 w-4 text-zinc-500"
          }
        />
      </div>
      <div className="mt-3 font-display text-3xl font-semibold tracking-tight text-zinc-100">
        {value}
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px]">
        {delta && <span className="font-mono text-zinc-400">{delta}</span>}
        {hint && <span className="text-zinc-500">{hint}</span>}
      </div>
    </Panel>
  );
}

function OverviewPage() {
  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: () => api.listSites() });
  const formsQ = useQuery({ queryKey: ["forms"], queryFn: () => api.listForms() });
  const statsQ = useQuery({ queryKey: ["stats", 7], queryFn: () => api.stats(7) });
  const recentQ = useQuery({ queryKey: ["recent", 20], queryFn: () => api.recent(20) });

  const sites = sitesQ.data?.sites ?? [];
  const forms = formsQ.data?.forms ?? [];
  const stats = statsQ.data?.results ?? [];
  const recent = recentQ.data?.results ?? [];

  // Aggregate KPIs from the 7-day stats payload.
  const totals = stats.reduce(
    (acc, r) => {
      acc.total += r.n;
      if (r.outcome === "sent") acc.sent += r.n;
      else acc.blocked += r.n;
      return acc;
    },
    { total: 0, sent: 0, blocked: 0 },
  );
  const deliverability =
    totals.total > 0 ? (totals.sent / totals.total) * 100 : 100;

  // Build a 24-bucket sent vs failed chart from recent events.
  const buckets = Array.from({ length: 24 }, () => ({ sent: 0, fail: 0 }));
  for (const e of recent) {
    const h = new Date(e.ts * 1000).getUTCHours();
    if (e.outcome === "sent") buckets[h].sent++;
    else buckets[h].fail++;
  }
  const maxBucket = Math.max(1, ...buckets.map((b) => b.sent + b.fail));

  // Per-form aggregation for the site health strip.
  const sentByForm = new Map<string, number>();
  for (const r of stats) {
    if (r.outcome === "sent") {
      sentByForm.set(r.site_key, (sentByForm.get(r.site_key) ?? 0) + r.n);
    }
  }
  const sentBySite = new Map<string, number>();
  const formsBySite = new Map<string, number>();
  for (const f of forms) {
    formsBySite.set(f.siteKey, (formsBySite.get(f.siteKey) ?? 0) + 1);
    const sent = sentByForm.get(f.formKey) ?? 0;
    sentBySite.set(f.siteKey, (sentBySite.get(f.siteKey) ?? 0) + sent);
  }

  const activeSites = sites.filter((s) => s.enabled).length;
  const activeForms = forms.filter((f) => f.enabled).length;
  const firstForm = forms[0];

  return (
    <AppShell title="Fleet overview" subtitle="All channels, live">
      <PageHeader
        eyebrow="overview · live"
        title="Fleet overview"
        description={`Monitoring ${activeSites} active ${activeSites === 1 ? "site" : "sites"} and ${activeForms} ${activeForms === 1 ? "form" : "forms"} in production.`}
        actions={
          <div className="flex items-center gap-2 rounded-md border border-border bg-surface/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
            <StatusDot status="healthy" pulse />
            live
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi
          label="Active sites"
          value={String(activeSites).padStart(2, "0")}
          hint={`${sites.length - activeSites} disabled`}
          icon={Activity}
        />
        <Kpi
          label="Sent (7d)"
          value={
            totals.sent >= 1000
              ? `${(totals.sent / 1000).toFixed(1)}k`
              : String(totals.sent)
          }
          delta={`${totals.total} total events`}
          icon={Send}
          tone="success"
        />
        <Kpi
          label="Blocked (7d)"
          value={totals.blocked.toLocaleString()}
          delta="turnstile · origin · rate"
          icon={ShieldAlert}
          tone="ember"
        />
        <Kpi
          label="Deliverability"
          value={`${deliverability.toFixed(1)}%`}
          hint={totals.total === 0 ? "no traffic yet" : "sent / total"}
          icon={TrendingUp}
          tone="success"
        />
      </div>

      <div className="mt-6 grid grid-cols-12 gap-6">
        <Panel className="fade-up col-span-12 p-6 lg:col-span-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="font-display text-sm font-semibold text-zinc-100">
                Delivery outcomes
              </h3>
              <p className="mt-0.5 text-[11px] text-zinc-500">
                Last 24 hours · sent vs failed per UTC hour
              </p>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <span className="inline-block h-2 w-2 rounded-sm bg-zinc-400" /> sent
              </div>
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <span className="inline-block h-2 w-2 rounded-sm bg-ember" /> failed
              </div>
            </div>
          </div>

          <div className="flex h-48 items-end justify-between gap-1.5">
            {buckets.map((b, i) => {
              const total = b.sent + b.fail;
              const totalPct = (total / maxBucket) * 100;
              const failPct = total ? (b.fail / total) * 100 : 0;
              return (
                <div
                  key={i}
                  className="group relative flex-1 overflow-hidden rounded-t-sm bg-zinc-700/40 transition-colors hover:bg-zinc-700/70"
                  style={{ height: `${totalPct || 2}%` }}
                  title={`${String(i).padStart(2, "0")}:00 · ${b.sent} sent · ${b.fail} failed`}
                >
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-ember"
                    style={{ height: `${failPct}%` }}
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex justify-between font-mono text-[10px] text-zinc-600">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>now</span>
          </div>
        </Panel>

        <div className="col-span-12 space-y-6 lg:col-span-4">
          <Panel className="fade-up p-6">
            <h3 className="mb-4 font-display text-sm font-semibold text-zinc-100">
              Quick actions
            </h3>
            <div className="space-y-2">
              {[
                { label: "Manage sites", to: "/sites" as const },
                { label: "Configure forms", to: "/forms" as const },
                { label: "Live event stream", to: "/events" as const },
                { label: "Integration docs", to: "/integrations" as const },
              ].map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className="flex items-center justify-between rounded-md border border-zinc-700/60 px-3 py-2 text-xs text-zinc-300 transition-colors hover:border-zinc-500 hover:bg-surface/60 hover:text-zinc-100"
                >
                  {a.label}
                  <ArrowRight className="h-3 w-3 text-zinc-500" />
                </Link>
              ))}
            </div>
          </Panel>

          {firstForm && (
            <div className="fade-up">
              <CodeBlock
                language="HTML · embed"
                code={`<script src="${origin}/embed.js" defer></script>
<form data-mailapi="${firstForm.formKey}">
  <label>Name <input name="name" required /></label>
  <label>Email <input name="email" type="email" required /></label>
  <label>Message <textarea name="message" required></textarea></label>
  <button type="submit">Send</button>
</form>`}
              />
            </div>
          )}
        </div>

        {sites.length > 0 && (
          <div className="col-span-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {sites.map((s) => (
              <Link
                key={s.siteKey}
                to="/sites/$siteKey"
                params={{ siteKey: s.siteKey }}
                className="fade-up flex items-center gap-3 rounded-lg bg-surface/40 p-4 ring-1 ring-white/5 transition-colors hover:bg-surface"
              >
                <StatusDot status={s.enabled ? "healthy" : "neutral"} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-zinc-100">{s.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                    {s.enabled ? "active" : "disabled"} ·{" "}
                    {formsBySite.get(s.siteKey) ?? 0} forms ·{" "}
                    {sentBySite.get(s.siteKey) ?? 0} sent
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Panel className="fade-up col-span-12 overflow-hidden">
          <PanelHeader
            title="Recent events"
            subtitle="Last 20 deliveries and blocks across the fleet"
            action={
              <Link
                to="/events"
                className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-ember"
              >
                view all →
              </Link>
            }
          />
          <div className="overflow-x-auto">
            {recentQ.isLoading ? (
              <div className="flex items-center gap-2 px-6 py-8 text-xs text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> loading events…
              </div>
            ) : recent.length === 0 ? (
              <div className="px-6 py-8 text-xs text-zinc-500">
                No events recorded yet.
              </div>
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="px-6 py-3 font-medium">Time</th>
                    <th className="px-6 py-3 font-medium">Form key</th>
                    <th className="px-6 py-3 font-medium">Outcome</th>
                    <th className="px-6 py-3 font-medium">Geo / ASN</th>
                    <th className="px-6 py-3 text-right font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-xs">
                  {recent.slice(0, 8).map((e, i) => (
                    <tr
                      key={`${e.ts}-${i}`}
                      className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.03]"
                    >
                      <td className="px-6 py-3 text-zinc-500">{fmtTsShort(e.ts)}</td>
                      <td className="px-6 py-3 text-zinc-200">
                        {truncateMid(e.site_key, 10)}
                      </td>
                      <td className="px-6 py-3">
                        <OutcomeBadge outcome={e.outcome} />
                      </td>
                      <td className="px-6 py-3 text-zinc-500">
                        {e.country ?? "—"}{" "}
                        <span className="text-zinc-700">/</span>{" "}
                        {e.asn ? `AS${e.asn}` : "—"}
                      </td>
                      <td className="px-6 py-3 text-right text-zinc-400">
                        {e.reason ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}
